import { MouseEvent, Key, useRef, useState } from 'react';
import { Listbox, ListboxItem } from '@ds';
import classnames from 'classnames';
import { NestedDropdownPopover } from './NestedDropdownPopover';
import { MenuNodeProps } from './types';

export const DropdownActionList = ({
  items,
  disabledKeys,
  selectedKeys,
  closeRoot,
  onAction,
  classNames: slots,
}: MenuNodeProps) => {
  const [openedKey, setOpenedKey] = useState<Key | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleSubmenuMouseLeave = (event: MouseEvent<HTMLElement>) => {
    const nextElement = event.relatedTarget;

    if (!(nextElement instanceof Node)) {
      setOpenedKey(null);
      return;
    }

    const isMovingToTrigger = triggerRef.current?.contains(nextElement);
    const isMovingToContent = contentRef.current?.contains(nextElement);

    if (isMovingToTrigger || isMovingToContent) {
      return;
    }

    setOpenedKey(null);
  };

  return (
    <Listbox
      aria-label="Nested dropdown menu"
      disabledKeys={disabledKeys || []}
      selectedKeys={selectedKeys || []}
      selectionMode="single"
      className={classnames('min-w-50 p-0', slots?.listbox)}
      classNames={{
        base: classnames('p-0', slots?.listboxBase),
        list: classnames('gap-0.5', slots?.listboxList),
      }}
    >
      {items.map((item) => {
        const hasChildren = Boolean(item.children?.length);
        const isOpened = openedKey === item.key;

        return (
          <ListboxItem
            key={item.key}
            textValue={String(item.key)}
            description={item.description}
            startContent={item.startContent}
            endContent={hasChildren ? null : item.endContent}
            color={item.color}
            className={classnames(
              'w-full data-[hover=true]:bg-base-50 p-0',
              !hasChildren && 'p-2 rounded-lg [&>svg]:w-4 [&>svg]:h-4 *:text-m-r!',
              item.color === 'primary' ? '*:text-base-5' : '*:text-danger-40',
              hasChildren && isOpened && 'bg-base-50',
              item?.className,
              slots?.item,
            )}
            classNames={{
              base: classnames(slots?.itemBase),
              title: classnames(slots?.itemTitle),
              description: classnames(slots?.itemDescription),
              selectedIcon: hasChildren ? 'hidden' : undefined,
            }}
            onMouseEnter={() => {
              if (hasChildren) setOpenedKey(item.key);
            }}
            onMouseLeave={() => {
              if (hasChildren) setOpenedKey(null);
            }}
            onPress={() => {
              if (hasChildren) {
                return;
              }

              onAction?.(item.key);
              closeRoot();
            }}
          >
            {!hasChildren && item.label}
            {hasChildren && (
              <NestedDropdownPopover
                triggerRef={triggerRef}
                contentRef={contentRef}
                isOpen={isOpened}
                onOpenChange={(nextIsOpen) => {
                  setOpenedKey(nextIsOpen ? item.key : null);
                }}
                onClose={handleSubmenuMouseLeave}
                classNames={slots}
                trigger={item.label}
              >
                <DropdownActionList
                  items={item.children ?? []}
                  disabledKeys={disabledKeys}
                  selectedKeys={selectedKeys}
                  closeRoot={closeRoot}
                  onAction={onAction}
                  classNames={slots}
                />
              </NestedDropdownPopover>
            )}
          </ListboxItem>
        );
      })}
    </Listbox>
  );
};
